import { useEffect, useMemo, ComponentType, ReactElement } from "react";
import { useRouter } from "next/router";
import Error from "next/error";
import { Spin, message } from "antd";
import { Loading3QuartersOutlined } from "@ant-design/icons";
import {
  Step,
  Flow,
  Skin,
  OnboardingApplication,
  FinancialBrand,
} from "@dreambigger/shared/src/types";
import { LocalStorage } from "@dreambigger/shared/src/utils";
import {
  changeStepForward,
  isAuthorizedAdvance,
} from "../utils/changeStepForward";

// Define the StepProps interface to be used by all step components
export interface StepProps {
  step: Step;
  brand: FinancialBrand;
  flow: Flow;
  progress: number;
  application: OnboardingApplication | undefined;
}
import {
  StepAddress,
  StepAdditionalContact,
  StepInitialDeposit,
  StepSuccess,
  StepCustom,
  StepPayment,
  StepPaymentStatus,
  StepPlaidAuth,
  StepPlaidIdv,
  StepSocureIdv,
  StepCar,
  StepReview,
  StepCustomCollections,
  StepLoanRate,
  StepIdvVouched,
  StepDocusign,
  StepShareProducts,
  StepCdInterest,
  StepExistingMember,
  StepExistingCoreAccounts,
  StepExistingMemberShares,
  StepCharitableOrganizationSearch,
  StepCIFLocator,
  StepCIFBusinessLocator,
  StepAtomic,
  StepTransition,
  StepTruStage,
  StepDocument,
  StepRepay,
  StepCrossSell,
} from "../steps";

import { useAuthToken } from "../passwordless-auth";
import { useApplication } from "../api";
import { emailKey, phoneKey, skipAheadKey } from "../pages/flows/[flowId]";

const spinnerIcon = <Loading3QuartersOutlined spin />;

// Get the progress of the current step
const getStepProgress = (currentSkin: Skin, stepSlug: string) => {
  const totalSteps = currentSkin.steps.length + 1;
  const stepIndex =
    currentSkin.steps.findIndex((step) => step.slug === stepSlug) + 1;
  return Math.round((stepIndex / totalSteps) * 100);
};

// Helper function to create a step component with proper key for React identity
const createStepComponent = (
  Component: ComponentType<StepProps>,
  stepProps: StepProps
): ReactElement => {
  return <Component key={stepProps.step.slug} {...stepProps} />;
};

// We are using this object instead of a switch statement for better maintainability and preventing rare save errors from reused components.
const stepComponents: Record<string, ComponentType<StepProps>> = {
  "additional-contact": StepAdditionalContact,
  address: StepAddress,
  "idv-vouched": StepIdvVouched,
  "initial-deposit": StepInitialDeposit,
  payment: StepPayment,
  "payment-status": StepPaymentStatus,
  plaid: StepPlaidAuth,
  "plaid-auth": StepPlaidAuth,
  "plaid-idv": StepPlaidIdv,
  "socure-idv": StepSocureIdv,
  car: StepCar,
  review: StepReview,
  success: StepSuccess,
  "loan-rate": StepLoanRate,
  docusign: StepDocusign,
  "share-products": StepShareProducts,
  "cd-interest": StepCdInterest,
  "existing-member": StepExistingMember,
  "existing-core-accounts": StepExistingCoreAccounts,
  "existing-member-shares": StepExistingMemberShares,
  "charitable-organization-search": StepCharitableOrganizationSearch,
  "cif-locator": StepCIFLocator,
  "cif-business-locator": StepCIFBusinessLocator,
  atomic: StepAtomic,
  transition: StepTransition,
  truStage: StepTruStage,
  document: StepDocument,
  "cross-sell": StepCrossSell,
  repay: StepRepay,
};

// Render non-start step components based on type
const getStepElement = (
  step: Step,
  skin: Skin,
  flow: Flow,
  application: OnboardingApplication | undefined
): ReactElement => {
  const stepProps: StepProps = {
    step,
    brand: skin.brand,
    flow,
    progress: getStepProgress(skin, step.slug),
    application: application,
  };

  // Guard against undefined step or step.type
  if (!step || !step.type) {
    return <Error statusCode={404} />;
  }

  // Variant-based dispatch for `custom`. Route to the entries-based
  // collection renderer only when `variant === "collection"` is set;
  // everything else (single-record and multi-row custom configs) falls
  // through to the default StepCustom renderer.
  const customAssets = step.assets as { variant?: string };
  const Component =
    step.type === "custom"
      ? customAssets.variant === "collection"
        ? StepCustomCollections
        : StepCustom
      : stepComponents[step.type];

  // Guard against unknown step types
  if (!Component) {
    return <Error statusCode={404} />;
  }

  return createStepComponent(Component, stepProps);
};

export type StepRouterProps = {
  step: Step;
  skin: Skin;
  flow: Flow;
  startStep: Step | undefined;
};

export default function StepRouter({
  step,
  skin,
  flow,
  startStep,
}: StepRouterProps) {
  const router = useRouter();
  const { prefillRequestUuid, ...otherQueryParams } = router.query;
  const tokenHelper = useAuthToken(flow.financialInstitution.id);
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const localStorage = useMemo(
    () => new LocalStorage(flow.financialInstitution.id),
    [flow.financialInstitution.id]
  );

  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );

  // --- FORWARD-JUMP GUARD ---
  // visitedPath records the order steps were actually submitted. If the user
  // jumps FORWARD via the browser back/forward button to a step they could not
  // have reached by a single controlled step from their last submitted step
  // (e.g. backing into the review screen after changing a conditional answer),
  // the deferred-branching algorithm reads the whole index gap as one skip and
  // prunes still-valid steps. Snap them back to their true position and ask
  // them to walk forward so the path rebuilds one step at a time.
  //
  // A routing-driven advance (changeStepForward, fired on submit) legitimately
  // skips array positions when a conditional branch changes — that's the case
  // skip-gap is meant to handle. Such advances mark their destination as
  // authorized, so the guard lets them through; only un-authorized forward
  // jumps (browser navigation, not a submit) are blocked.
  useEffect(() => {
    const visitedPath = application?.visitedPath;
    const lastVisitedSlug = application?.lastVisitedSlug;
    if (!visitedPath || !lastVisitedSlug) return; // legacy/fresh apps: no gate

    const curIdx = visitedPath.indexOf(step.slug);
    const lastIdx = visitedPath.indexOf(lastVisitedSlug);
    if (lastIdx === -1) return; // can't locate current position; don't gate

    const tail = visitedPath.length - 1;

    // Forward jump = landing somewhere unreachable by a single controlled step
    // from the last submitted step:
    //  - onto a later step already in the path (curIdx > lastIdx + 1), or
    //  - onto a step NOT in the path while sitting behind the furthest submit
    //    (curIdx === -1 && lastIdx < tail) — e.g. the review screen, which is
    //    never in visitedPath. Submitting/leaving here truncates the still-
    //    valid tail. (At the frontier, lastIdx === tail, a new step is normal
    //    forward progress, so it's allowed.)
    const isForwardJump =
      (curIdx !== -1 && curIdx > lastIdx + 1) ||
      (curIdx === -1 && lastIdx < tail);
    if (!isForwardJump) return;

    // Routing-approved advance (a real submit) — allow the skip.
    if (isAuthorizedAdvance(step.slug)) return;

    message.error({
      content:
        "Application data changed. Please proceed through the application to continue.",
      key: "forward-jump-guard", // dedupe so rapid re-fires show one toast
    });

    router.replace({ query: { ...router.query, stepSlug: lastVisitedSlug } });
  }, [step.slug, application?.visitedPath, application?.lastVisitedSlug]);

  // Extract brandVariantId from URL path if present
  // Path format: /flows/[flowId]/v/[brandVariantId]
  const pathSegments = router.asPath.split("/");
  const variantIndex = pathSegments.indexOf("v");
  const brandVariantId =
    variantIndex !== -1 && pathSegments[variantIndex + 1]
      ? pathSegments[variantIndex + 1].split("?")[0] // Remove query params if any
      : undefined;

  // Filter out UTM Params from router query params.
  const utmParams = Object.keys(router.query)
    .filter((key) => key.toLowerCase().startsWith("utm_"))
    .reduce((utmObj: Record<string, any>, key) => {
      utmObj[key] = router.query[key];
      return utmObj;
    }, {});

  // --- CREATE APPLICATION ---
  // Create application on step load if conditions are met.
  useEffect(() => {
    // Wait for startStep to be defined
    if (!startStep) return;
    // Define if step is valid to create application:
    // Must not be success step and must be the first intended step in the workflow (following landing page or without a previousSlug)
    const stepIsValidToCreateApplication =
      step.type !== "success" &&
      (!step.assets.previousSlug ||
        step.assets.previousSlug === startStep.slug);
    // Check that step is valid, that application doesn't already exist, and that the token is not expired before creating application.
    if (
      !stepIsValidToCreateApplication ||
      !applicationHelper.data ||
      application ||
      tokenHelper.jwtIsExpired()
    ) {
      return;
    }
    // Create the application
    const emailField = {
      [emailKey]: localStorage.getItem(emailKey),
    };
    const phoneField = {
      [phoneKey]: localStorage.getItem(phoneKey),
    };

    applicationHelper.create(
      flow.id,
      {
        slug: startStep.slug,
        type: startStep.type,
        fields:
          flow.financialInstitution.loginMode === "email"
            ? emailField
            : phoneField,
      },
      utmParams,
      brandVariantId,
      {
        prefillRequestUuid: Array.isArray(prefillRequestUuid)
          ? prefillRequestUuid[0]
          : prefillRequestUuid,
      }
    );

    //remove prefillRequestUuid from query params if it exists
    router.replace(
      {
        query: {
          ...otherQueryParams,
        },
      },
      {
        query: {
          ...otherQueryParams,
        },
      },
      {
        shallow: true,
      }
    );
  }, [
    applicationHelper.data,
    application,
    step,
    startStep,
    tokenHelper.jwtIsExpired(),
  ]);

  // Close application if draft application exists and prefillRequestUuid is present in the query parameters
  useEffect(() => {
    if (!applicationHelper.data || !application || !prefillRequestUuid) {
      return;
    }
    applicationHelper.close(application);
  }, [applicationHelper.data, application, prefillRequestUuid]);

  // --- SKIP AHEAD UPON LOGIN WITH OTPID ---
  // This process is initiated on start.tsx
  // Only begins if a user tries to log in with an otpId.
  useEffect(() => {
    if (!application) {
      return;
    }
    const skipAhead = localStorage.getItem(skipAheadKey);
    if (skipAhead !== "true") {
      return;
    }
    localStorage.removeItem(skipAheadKey);

    // If lastStepVisited is undefined or the same as the first step or current step, do nothing
    // Otherwise, send the user to their most recently visited slug.
    if (
      !application?.lastVisitedSlug ||
      application.lastVisitedSlug === startStep?.slug ||
      application.lastVisitedSlug === step.slug
    ) {
      return;
    }
    changeStepForward(application?.lastVisitedSlug);
  }, [application, step, startStep]);

  // --- RESTORE UTM PARAMS ---
  // If UTM params have been removed from the URL for any reason,
  // restore them from the application record so GTM URL-based triggers work.
  useEffect(() => {
    if (!application?.utmParams) return;

    // Check if any saved UTM params are missing from the current URL
    const missingUtms = Object.keys(application.utmParams).filter(
      (key) => !(key in router.query)
    );
    if (missingUtms.length === 0) return;

    // Merge: application UTMs fill in gaps, existing URL params take precedence
    router.replace(
      { query: { ...application.utmParams, ...router.query } },
      undefined,
      { shallow: true }
    );
  }, [application, router.query]);

  return prefillRequestUuid ? (
    <Spin indicator={spinnerIcon} />
  ) : (
    getStepElement(step, skin, flow, application)
  );
}
